type Primitive = boolean | number | string;

/**
 * Removes duplicate elements from an array, ensuring uniqueness.
 *
 * If the array contains primitive values, it filters out duplicates directly.
 * If the array contains objects, it filters out duplicates based on a specified property.
 *
 * @template T - The type of elements in the array. Defaults to `Primitive`.
 * @param array - The array to be filtered for unique elements.
 * @param filterBy - The property name to use for filtering uniqueness when the array contains objects.
 *                   Defaults to `'id'`.
 * @returns A new array containing only unique elements.
 */
export function uniqArray<T = Primitive>(array: Array<T>, filterBy: string = 'id'): Array<T> {
    if (isPrimitive(array[0])) {
        // Assuming all elements in the array is of the same type
        return array.filter((v, i, a) => a.indexOf(v) === i);
    } else {
        return array.filter((v, i, a) => a.findIndex(va => va[filterBy] === v[filterBy]) === i);
    }
}

/**
 * Determines if the provided value is a primitive type.
 *
 * A primitive type is any value that is not an object and does not have methods.
 * This includes `string`, `number`, `bigint`, `boolean`, `symbol`, `null`, and `undefined`.
 *
 * @param a - The value to check.
 * @returns `true` if the value is a primitive type, otherwise `false`.
 */
export function isPrimitive(a: unknown): boolean {
    return a !== Object(a);
}
